/**
 * Guías de integración — Structura Market (features reales).
 */
import type { HelpArticle } from "@/lib/help-content";

export const INTEGRATION_HELP_ARTICLES: HelpArticle[] = [
  {
    id: "payment-center-overview",
    title: "Centro de pagos (/app/pagos)",
    section: "Integraciones",
    sectionIcon: "💬",
    keywords: ["pagos", "payway", "mercadopago", "links", "qr", "pedidos"],
    url: "/app/pagos",
    content: `
El **Centro de pagos** centraliza cobros online del local: Payway, Mercado Pago, links y transacciones.

**Pasos:**
1. Menú → **Pagos**
2. Activá un medio de cobro (Payway / Mercado Pago)
3. Probá con un pedido de prueba
`,
  },
  {
    id: "payway-connect",
    title: "Conectar Payway",
    section: "Integraciones",
    sectionIcon: "💬",
    keywords: ["payway", "decidir", "tarjeta", "sandbox"],
    url: "/app/pagos",
    content: `
1. **Pagos** → Payway → activar
2. API pública + privada (sandbox primero)
3. Guardá y generá link de cobro de prueba

**Errores:** claves de prueba en producción, cobro que no se confirma.
`,
  },
  {
    id: "mercadopago-connect",
    title: "Conectar Mercado Pago",
    section: "Integraciones",
    sectionIcon: "💬",
    keywords: ["mercadopago", "access token", "public key"],
    url: "/app/pagos",
    content: `
1. [Mercado Pago Developers](https://www.mercadopago.com.ar/developers) → Credenciales
2. **Pagos** → pegar Access Token y Public Key
3. En Mercado Pago, configurá la confirmación de pagos hacia tu tienda Structura (consultá el panel)

Usá credenciales de **prueba** en desarrollo.
`,
  },
  {
    id: "storefront-branding",
    title: "Personalizar tienda pública",
    section: "Tienda pública",
    sectionIcon: "🌐",
    keywords: ["logo", "colores", "marca", "hero"],
    url: "/app/settings",
    content: `
**Configuración → Personalización:** logo, colores, horarios de atención y datos de contacto.

Vista previa en \`/shop/tu-slug\`.
`,
  },
  {
    id: "custom-domain-setup",
    title: "Dominio propio",
    section: "Configuración",
    sectionIcon: "⚙️",
    keywords: ["dominio", "dns", "cname"],
    url: "/app/settings",
    content: `
Ingresá dominio en **Configuración → Publicar**, creá CNAME hacia **food.structura.com.ar** y esperá verificación.

Estado \`verified\` = listo para compartir.
`,
  },
  {
    id: "webhook-troubleshooting",
    title: "Pagos que no se confirman",
    section: "Integraciones",
    sectionIcon: "💬",
    keywords: ["pago", "pendiente", "confirmación"],
    content: `
Si el pedido queda en pago pendiente: revisá que el medio de cobro esté configurado y reenviá la confirmación desde el panel de Payway o Mercado Pago.
`,
  },
  {
    id: "pesaje-setup",
    title: "Configurar balanza / pesaje",
    section: "Pesaje",
    sectionIcon: "⚖️",
    keywords: ["pesaje", "balanza", "kg", "productos pesables"],
    url: "/app/pesaje",
    content: `
1. Creá productos **por peso** en **Productos**
2. **Pesaje** → seleccioná producto y registrá peso
3. El precio se calcula según precio/kg

Ideal para carnicerías, dietéticas y rotiserías.
`,
  },
  {
    id: "modo-connect",
    title: "Conectar MODO (QR)",
    section: "Integraciones",
    sectionIcon: "💬",
    keywords: ["modo", "qr"],
    url: "/app/pagos",
    content: `Activá MODO en **Pagos**, pegá Client ID/Secret y probá QR sandbox.`,
  },
  {
    id: "uala-connect",
    title: "Conectar Ualá Bis",
    section: "Integraciones",
    sectionIcon: "💬",
    keywords: ["uala", "ualá", "bis", "checkout", "client id"],
    url: "/app/pagos",
    content: `
1. **Pagos** → Ualá Bis → activar
2. Username, Client ID y Client Secret (STAGE para pruebas)
3. **Ambiente:** vacío o sandbox = STAGE · production = cobros reales
4. Tarjeta test: Visa \`4507990000001026\`, CVV 830, venc. 03/25

El cliente paga en el checkout de Ualá; Structura recibe el webhook automáticamente.
`,
  },
  {
    id: "email-smtp-byoc",
    title: "Email propio (SMTP / Resend)",
    section: "Integraciones",
    sectionIcon: "💬",
    keywords: ["smtp", "resend", "email"],
    url: "/app/settings",
    content: `Configuración → Email: Resend BYO o SMTP (Gmail app password, Zoho, Outlook).`,
  },
  {
    id: "shipnow-connect",
    title: "Cómo configurar logística",
    section: "Logística",
    sectionIcon: "🚚",
    keywords: ["shipnow", "envíos", "logística", "sandbox", "token", "configurar", "shipping_shipnow"],
    url: "/app/settings",
    content: `
1. **Configuración → Envíos** → tarjeta **Shipnow**
2. Pegá tu API token (sandbox o producción)
3. Elegí modo **Proveedor**, **Híbrido** o **Manual**
4. Configurá **peso default por pedido** (gramos) y tarifas manuales de respaldo
5. Activá el proveedor y usá **Probar conexión**
6. Opcional: **Sincronizar sucursales** para puntos de retiro

En **Productos → Logística** podés cargar peso por ítem (opcional).

Structura no opera tu logística: vos conectás tu cuenta BYO.
`,
  },
  {
    id: "shipping-costs",
    title: "Cómo funcionan los costos de envío",
    section: "Logística",
    sectionIcon: "🚚",
    keywords: ["envío", "cotización", "peso", "checkout", "cp", "tarifa"],
    url: "/app/settings",
    content: `
## En checkout (tienda pública)

1. El cliente elige **domicilio** e ingresa **código postal**
2. Structura suma el peso del carrito (gramos por producto o peso default del comercio)
3. Shipnow cotiza carrier, ETA y precio
4. El envío se **cobra junto con los productos** antes de pagar

## Peso

- Productos con **peso en gramos** → se usa ese valor × cantidad
- Productos **por kilo** → el peso del pedido es la cantidad en kg
- Sin peso configurado → **peso default por pedido** (Configuración → Envíos)

## Si Shipnow falla

- Modo **Híbrido**: tarifa manual por zona (CABA / GBA / Interior)
- Modo **Manual**: solo tarifas manuales

El shipment en Shipnow se crea **recién cuando el pedido está pagado**, no en checkout.
`,
  },
  {
    id: "shipnow-create-shipment",
    title: "Cómo generar envíos",
    section: "Logística",
    sectionIcon: "🚚",
    keywords: ["envío", "shipment", "pedido", "shipnow"],
    url: "/app/pedidos",
    content: `
1. El cliente cotiza envío en checkout (CP + opción de carrier)
2. Cuando el pedido está **pagado**, abrilo en **Pedidos**
3. En la sección **Envío por correo** → **Crear envío**
4. Se guarda tracking y estado en el pedido

Requiere plan **Pro** para etiquetas y tracking automático.
`,
  },
  {
    id: "shipnow-print-label",
    title: "Cómo imprimir etiquetas",
    section: "Logística",
    sectionIcon: "🚚",
    keywords: ["etiqueta", "label", "pdf", "shipnow", "qz", "imprimir"],
    url: "/app/pedidos",
    content: `
1. Creá el envío desde el pedido pagado (**Crear envío**)
2. Tocá **Imprimir etiqueta** — la PDF va directo a la impresora vía **QZ Tray**
3. No se abre descarga ni pestaña nueva

**Requisitos:** QZ Tray instalado, impresora de etiquetas configurada en Configuración → Impresoras.

Si falla, revisá token Shipnow, que el envío exista en tu panel Shipnow y el rol de impresora de etiquetas.
`,
  },
  {
    id: "shipnow-tracking",
    title: "Cómo funciona el tracking",
    section: "Logística",
    sectionIcon: "🚚",
    keywords: ["tracking", "seguimiento", "estado", "envío", "shipping_tracking"],
    url: "/app/pedidos",
    content: `
- **Seguir envío** abre el link del carrier
- Structura sincroniza estados cada **30 minutos** (cron)
- Estados: en tránsito, entregado, fallido, devuelto

El cliente ve el estado cuando compartís el link de tracking.
`,
  },
  // ── Treggo ──────────────────────────────────────────────────────────────────
  {
    id: "conectar-treggo",
    title: "Cómo configurar Treggo",
    section: "Logística",
    sectionIcon: "🚚",
    keywords: ["treggo", "envíos", "logística", "api secret", "webhook", "configurar", "carrier", "treggo_shipping"],
    url: "/app/settings",
    content: `
1. **Configuración → Envíos** → tarjeta **Treggo**
2. Ingresá el **email** de tu cuenta Treggo y el **API Secret** (obtenelo en tu panel Treggo)
3. Completá la **dirección de origen** desde donde se retiran los paquetes
4. Opcionalmente activá **Modo desarrollo (sandbox)** para pruebas sin costo
5. Guardá → Structura registra el webhook automáticamente en Treggo
6. Usá **Probar conexión** para validar credenciales
7. Activá el switch para que Treggo empiece a cotizar en checkout

**Nota:** Treggo y Shipnow pueden coexistir. Structura usa el primer proveedor activo.
`,
  },
  {
    id: "treggo-webhook",
    title: "Webhook de Treggo — actualizaciones automáticas",
    section: "Logística",
    sectionIcon: "🚚",
    keywords: ["treggo", "webhook", "tracking", "estado", "automático"],
    url: "/app/settings",
    content: `
Al guardar credenciales Treggo, Structura registra un webhook en tu cuenta Treggo automáticamente.

Esto permite recibir actualizaciones de estado en tiempo real sin polling:
- **shipment.status_changed** — cambio de estado general
- **shipment.delivered** — entregado al destinatario
- **shipment.failed** — fallo o intento fallido

Si el webhook no se registra (credenciales incorrectas), podés volver a guardar una vez corregidas.
`,
  },
  // ── Andreani ────────────────────────────────────────────────────────────────
  {
    id: "conectar-andreani",
    title: "Cómo configurar Andreani",
    section: "Logística",
    sectionIcon: "🚚",
    keywords: ["andreani", "envíos", "logística", "contrato", "configurar", "carrier", "domicilio", "sucursal", "andreani_shipping"],
    url: "/app/settings",
    content: `
1. **Configuración → Envíos** → tarjeta **Andreani**
2. Ingresá el **usuario** (email de cuenta Andreani) y la **contraseña** de la API
3. Completá el **código de cliente** (lo obtenés en tu cuenta Andreani)
4. Ingresá los contratos de envío:
   - **Contrato domicilio**: para entregas a domicilio
   - **Contrato sucursal**: para retiro en sucursal Andreani
5. Completá el **código postal de origen** (desde dónde despachás)
6. Opcionalmente activá **Modo sandbox** (usa api.qa.andreani.com, sin costo real)
7. Guardá → usá **Probar conexión** para validar
8. Activá el switch para habilitar cotización en checkout

**Requisito:** plan **Pro** con feature flag \`andreani_shipping\` activo.
`,
  },
  {
    id: "conectar-cabify",
    title: "Cómo configurar Cabify Logistics",
    section: "Logística",
    sectionIcon: "🚚",
    keywords: ["cabify", "envíos", "inmediato", "same day", "logística", "cabify_shipping"],
    url: "/app/settings",
    content: `
Ideal para **envío inmediato** y **same day**.

1. **Configuración → Envíos** → tarjeta **Cabify Logistics**
2. Creá cuenta en [Cabify Business](https://cabify.com/es/business) y generá API Keys (UUID + Secret)
3. Pegá Client ID y Secret; cargá lat/lon de origen
4. Probá conexión y activá

Docs: https://developers.cabify.com/docs/introduction

Al cotizar en checkout, Structura llama a \`/v3/parcels/estimate\` y suma el precio al total a pagar.

**Requisito:** feature flag \`cabify_shipping\` activo.
`,
  },
  {
    id: "conectar-uber-direct",
    title: "Cómo configurar Uber Direct",
    section: "Logística",
    sectionIcon: "🚚",
    keywords: ["uber", "uber direct", "envíos", "courier", "on-demand", "logística", "uber_direct_shipping"],
    url: "/app/logistica?provider=uber_direct",
    content: `
Courier **on-demand** de Uber: pickup en tu local y entrega al cliente ([docs](https://developer.uber.com/docs/deliveries/overview)).

1. **Logística → Uber Direct**
2. Dashboard [Uber Direct](https://direct.uber.com/) (modo Test) → Developer → copiá **Customer ID**, **Client ID**, **Client Secret**
3. Pegá credenciales + dirección de **pickup** del local
4. Probá conexión (OAuth \`eats.deliveries\`) y activá

Sandbox no genera couriers reales. Producción requiere aprobación Uber.

**Requisito:** feature flag \`uber_direct_shipping\` (Pro+).
`,
  },
  {
    id: "conectar-pedidosya-delivery",
    title: "Conectar PedidosYa Delivery",
    section: "Logística",
    sectionIcon: "🚚",
    keywords: ["pedidosya", "delivery", "courier", "partner portal", "delivery_provider_pedidosya"],
    url: "/app/logistica?provider=pedidosya",
    content: `
Prepará el provider mock y dejá credenciales listas para homologación Partner Portal.

**Fase 1 (mock):** Activá PedidosYa en Logística, modo Sandbox + Mock. Usá el simulador para cotizar, crear y avanzar estados sin red.

**Homologación:** Cuando tengas Partner Portal: pegá Base URL, Vendor/Chain/Integration Code, credenciales y Webhook Secret. Registrá la Webhook URL de Structura y corré Test Connection.
`,
  },
  {
    id: "conectar-rapiboy",
    title: "Conectar Rapiboy",
    section: "Logística",
    sectionIcon: "🚚",
    keywords: ["rapiboy", "última milla", "delivery_provider_rapiboy"],
    url: "/app/logistica?provider=rapiboy",
    content: `
Mock Fase 1 + credenciales cuando Rapiboy las provea (Postman API).

**Mock:** Activá Rapiboy en Logística y usá el simulador sin credenciales.

**Live:** Pegá Merchant ID, API Key, Base URL y webhook según la colección Postman pública de Rapiboy.
`,
  },
  {
    id: "conectar-correo-argentino",
    title: "Conectar Correo Argentino (MiCorreo)",
    section: "Logística",
    sectionIcon: "🚚",
    keywords: ["correo argentino", "micorreo", "correo_argentino_shipping"],
    url: "/app/logistica?provider=correo_argentino",
    content: `
Cotizá e importá envíos con la API REST MiCorreo (JWT).

**Credenciales:** Pedí a Correo usuario/password API (Basic Auth para /token) y tu customerId MiCorreo (/users/validate). QA: apitest.correoargentino.com.ar/micorreo/v1.

**Uso:** En Logística → Correo Argentino completá API user/password, customerId y CP origen. Mock mode para probar sin red; desactivá para live.
`,
  },
  {
    id: "conectar-oca-epak",
    title: "Conectar OCA ePak",
    section: "Logística",
    sectionIcon: "🚚",
    keywords: ["oca", "epak", "oca_epak_shipping", "paquetería"],
    url: "/app/logistica?provider=oca",
    content: `
Cotizá y despachá con OCA (ePak ASMX). Incluye datos oficiales de prueba.

**Datos de prueba:** En Logística → OCA usá «Cargar datos de prueba OCA» (usr test@oca.com.ar, cuenta 111757/001, CUIT 30-53625919-4, operativa 64665). Docs: https://developers.oca.com.ar/epak.html

**Producción:** Desactivá mock, completá usr/password ePak reales, CUIT, N° de cuenta, operativas y dirección de origen. Sandbox QA = integraciones.ocadev.com.ar.
`,
  },



  {
    id: "conectar-zipnova",
    title: "Cómo configurar Zipnova Envíos",
    section: "Logística",
    sectionIcon: "🚚",
    keywords: ["zipnova", "zippin", "envíos", "logística", "zipnova_shipping"],
    url: "/app/settings",
    content: `
Multi-carrier (correo / mensajería nacional).

1. **Configuración → Envíos** → tarjeta **Zipnova**
2. En Zipnova: Configuración → Integraciones → credenciales API v2 (Key + Secret) y anotá account_id
3. Pegá Key, Secret y Account ID; opcional Origin ID
4. Probá conexión y activá
5. Copiá la URL de webhook de la pantalla (incluye tu slug) y creá 2 webhooks en Zipnova: topics \`status\` y \`shipment\`

Docs: https://docs.zipnova.com/envios/

La cotización usa \`POST /v2/shipments/quote\` y el importe se suma al pedido. El slug del tenant es inmutable: aísla los webhooks entre cuentas.

**Requisito:** feature flag \`zipnova_shipping\` activo.
`,
  },

  {
    id: "logistica-para-que-nichos",
    title: "¿Para qué tipo de negocio sirve la logística de envíos?",
    section: "Logística",
    sectionIcon: "🚚",
    keywords: ["treggo", "andreani", "shipnow", "nicho", "tipo negocio", "restaurante", "almacén", "merchant", "envío", "correo"],
    url: "/app/settings",
    content: `
La logística de correo (Shipnow, Treggo, Andreani) sirve para **enviar productos físicos por correo o servicio de mensajería a todo el país**.

## ✅ Aplica para

- **Almacén / Dietética**: productos secos, suplementos, naturales — ideal envío nacional
- **Estampados y bordados (Merchant)**: prendas personalizadas que se despachan al cliente
- **Non-food / Bazar**: productos que no requieren cadena de frío

## ⚠️ Parcial

- **Carnicería**: el envío de productos cárnicos refrigerados requiere carriers especializados en frío (no Shipnow/Treggo/Andreani)

## ❌ No aplica

- **Restaurante / Cafetería**: el delivery es local (motos propias, PedidosYa, Rappi) — no correo nacional
- **Panadería**: perecederos con entrega el mismo día — no correo
- **Productos digitales**: entrega automática por email/link — sin envío físico

> Si tu negocio combina envío local + correo nacional (ej: almacén que también hace delivery), podés tener ambos configurados: logística de correo para pedidos de zonas lejanas y delivery propio para zona local.
`,
  },
  {
    id: "andreani-contratos",
    title: "Contratos Andreani — domicilio vs sucursal",
    section: "Logística",
    sectionIcon: "🚚",
    keywords: ["andreani", "contrato", "domicilio", "sucursal", "modalidad"],
    url: "/app/settings",
    content: `
Andreani diferencia dos modalidades por contrato:

- **Domicilio**: envío a la dirección del cliente. Requiere contrato de modalidad domicilio.
- **Sucursal**: el cliente retira en una sucursal Andreani. Requiere contrato de modalidad sucursal.

Si solo tenés un contrato (ej: domicilio), dejá el campo de sucursal vacío y solo esa modalidad aparecerá en checkout.

Los números de contrato los encontrás en el panel web de Andreani bajo tu cuenta de cliente.
`,
  },
  // ── Cupones y promociones ──────────────────────────────────────────────────
  {
    id: "crear-cupon",
    title: "Cómo crear un cupón de descuento",
    section: "Fidelización",
    sectionIcon: "🎟️",
    keywords: ["cupon", "descuento", "porcentaje", "monto fijo", "codigo", "discount_coupons", "cupones"],
    url: "/app/cupones",
    content: `
Los cupones de descuento permiten ofrecer un **% o monto fijo** a tus clientes.

**Pasos:**
1. Menú → **Cupones**
2. Clic en **Nuevo cupón**
3. Completar el wizard en 5 pasos: Tipo → Alcance → Límites → Pop-up → Revisar
4. Activar al guardar (o dejarlo inactivo y activar después)

**El código se ingresa en el checkout** — el cliente lo escribe en el campo "¿Tenés un cupón?" antes de confirmar el pedido.

El descuento se aplica después de gift cards y puntos de fidelización.
`,
  },
  {
    id: "tipos-cupon",
    title: "Cupón porcentual vs monto fijo",
    section: "Fidelización",
    sectionIcon: "🎟️",
    keywords: ["cupon", "porcentaje", "monto", "fijo", "scope", "productos", "categorias"],
    url: "/app/cupones",
    content: `
**Porcentual**: descuenta un % del total elegible. Ej: 15% OFF → si el carrito es $1000, el descuento es $150.

**Monto fijo**: descuenta un importe fijo. Ej: $200 OFF. Si el total elegible es menor al monto fijo, el descuento se limita al total.

**Alcance:**
- **Toda la tienda** → aplica sobre el subtotal completo.
- **Productos específicos** → aplica solo sobre los ítems de la lista, el resto paga precio normal.
- **Categorías** → ídem pero por categoría.

Si un cliente ingresa un cupón y ninguno de sus ítems califica, el sistema retorna error "scope_mismatch".
`,
  },
  {
    id: "crear-promocion-3x2",
    title: "Crear una promoción 3x2 o 2x1",
    section: "Fidelización",
    sectionIcon: "🏷️",
    keywords: ["promocion", "3x2", "2x1", "automatica", "nxm", "carrito", "promotions_engine", "promociones"],
    url: "/app/promociones",
    content: `
Las promociones **se aplican automáticamente** al carrito — el cliente no necesita ingresar ningún código.

**3x2 (cheapest_free):** por cada 3 ítems elegibles, el más barato es gratis.

**2x1 (same_product):** por cada 2 unidades del mismo producto (variantes cuentan), una es gratis.

**Pasos:**
1. Menú → **Promociones**
2. Clic en **Nueva promoción**
3. Elegir preset 3x2 / 2x1 / 4x3 o configurar Nxm personalizado
4. Definir alcance, fechas, banner y (optionalmente) pop-up de primer ingreso

Las promociones activas se muestran en la **home** como cards y en la **LDP** como filtro "En promoción".
`,
  },
  {
    id: "como-aplican-promos",
    title: "¿Cómo se aplican los descuentos en el carrito?",
    section: "Fidelización",
    sectionIcon: "🏷️",
    keywords: ["descuento", "orden", "promo", "cupon", "gift card", "puntos"],
    url: "/app/cupones",
    content: `
El checkout aplica los descuentos en este orden:

1. **Gift card** (si el cliente tiene saldo)
2. **Puntos de fidelización** (si el cliente elige canjearlos)
3. **Cupón de descuento** (código ingresado manualmente)
4. **Promociones automáticas** (3x2, 2x1, etc. — sin código)

Cada ítem del carrito solo puede ser "reclamado" por una promo activa. Si hay varias promos, se procesan en orden de creación.

El total final nunca puede ser negativo — si la suma de descuentos supera el subtotal, se limita al subtotal.
`,
  },
  {
    id: "ga4-connect",
    title: "Conectar Google Analytics 4 (GA4)",
    section: "Integraciones",
    sectionIcon: "💬",
    keywords: ["ga4", "google analytics", "medicion", "trafico", "conversiones", "measurement id"],
    url: "/app/settings",
    content: `
**Qué resuelve:** te muestra cuánta gente visita tu tienda, de dónde viene y cuántas de esas visitas terminan en una compra.

**Cuándo conviene usarla:** desde el día uno, para saber si tus publicaciones en redes o tu inversión en anuncios realmente traen clientes.

**Requisitos previos:** ninguno si usás el modo gestionado. Si preferís tu propia cuenta de Google Analytics, necesitás crear una propiedad GA4 en [analytics.google.com](https://analytics.google.com) y copiar el **Measurement ID** (formato \`G-XXXXXXXX\`).

**Dónde encontrarla:** **Configuración → Medición → Google Analytics** (grupo Analítica).

**Configuración paso a paso:**
1. Abrí la tarjeta de Google Analytics y elegí el modo:
   - **Gestionado por Structura**: no necesitás nada tuyo, Structura provisiona una propiedad GA4 automáticamente y te muestra el Measurement ID activo.
   - **Tu Google Analytics**: activá el switch "Tu Google Analytics", pegá tu Measurement ID (\`G-XXXXXXXX\`) y guardá.
2. Guardá los cambios de la sección Medición.

**Qué debe completar el comercio:** nada si usa el modo gestionado; el Measurement ID propio si eligió modo custom.

**Qué sucede después de guardar:** el script de GA4 se inyecta automáticamente en tu tienda pública (no en el panel de administración) y empieza a registrar visitas y eventos de compra.

**Cómo verificar que quedó funcionando:** entrá a tu tienda pública desde otra pestaña, navegá un poco, y revisá en Google Analytics → Informes → Tiempo real que aparezca tu visita. Si usás el modo gestionado, el panel muestra el estado de "provisioning" y el ID activo.

**Errores frecuentes:**
- Pegar el ID de Universal Analytics viejo (\`UA-XXXXXXXX\`) en vez de un GA4 (\`G-XXXXXXXX\`) — el campo lo rechaza.
- Esperar datos inmediatos: Google Analytics puede tardar unos minutos en mostrar tráfico en tiempo real y hasta 24-48hs en reportes históricos.

**Límites o plan:** disponible desde plan Pro.

**Cómo desactivar o cambiar:** apagá el switch "Activar" de la tarjeta de Google Analytics, o cambiá el Measurement ID en cualquier momento — el cambio aplica en la próxima carga de la tienda pública.

**Preguntas frecuentes:**
- *¿Se carga en el panel de administración?* No, sólo en la tienda pública, salvo que actives "Solo en la tienda publicada" en off (afecta a todas las integraciones de medición).
- *¿Puedo tener GA4 y Meta Pixel activos al mismo tiempo?* Sí, todas las integraciones de esta sección son independientes entre sí.
`,
  },
  {
    id: "gtm-connect",
    title: "Conectar Google Tag Manager (GTM)",
    section: "Integraciones",
    sectionIcon: "💬",
    keywords: ["gtm", "google tag manager", "contenedor", "etiquetas"],
    url: "/app/settings",
    content: `
**Qué resuelve:** te deja administrar todos tus scripts de marketing (píxeles, analíticas, etiquetas de conversión) desde un solo contenedor de Google, sin depender de que Structura agregue cada integración nueva.

**Cuándo conviene usarla:** si tu agencia de marketing ya trabaja con GTM, o si necesitás agregar herramientas de medición que Structura no tiene como integración nativa.

**Requisitos previos:** una cuenta de Google Tag Manager con un contenedor creado (te da un ID con formato \`GTM-XXXXXXX\`).

**Dónde encontrarla:** **Configuración → Medición → Google Tag Manager** (grupo Etiquetas).

**Configuración paso a paso:**
1. En [tagmanager.google.com](https://tagmanager.google.com), copiá el ID de tu contenedor.
2. En Structura, abrí la tarjeta de Google Tag Manager, activá el switch y pegá el ID (\`GTM-XXXXXXX\`).
3. Guardá.

**Qué debe completar el comercio:** el ID del contenedor GTM.

**Qué sucede después de guardar:** el snippet de GTM se inyecta en la tienda pública. A partir de ahí, todo lo que configures dentro de tu contenedor de Google (píxeles, triggers, variables) se ejecuta en tu tienda sin tocar nada más en Structura.

**Cómo verificar que quedó funcionando:** con la extensión "Tag Assistant" de Google, o desde la vista previa del propio GTM, abrí tu tienda pública y confirmá que el contenedor se dispara.

**Errores frecuentes:**
- Confundir el ID del contenedor (\`GTM-XXXXXXX\`) con el ID de una etiqueta individual dentro de GTM.
- Activar GTM y además activar por separado GA4/Meta/etc. nativos con el mismo propósito, duplicando el disparo de eventos — si vas a manejar una integración desde GTM, no la actives también como integración nativa.

**Límites o plan:** disponible desde plan Pro.

**Cómo desactivar o cambiar:** apagá el switch de la tarjeta; el snippet deja de inyectarse en la próxima carga de la tienda.

**Preguntas frecuentes:**
- *¿GTM reemplaza a las demás integraciones de esta sección?* Puede, si preferís manejar todo desde tu contenedor — es una elección de quién administra los scripts, no una capa adicional obligatoria.
`,
  },
  {
    id: "meta-pixel-connect",
    title: "Conectar Meta Pixel (Facebook/Instagram)",
    section: "Integraciones",
    sectionIcon: "💬",
    keywords: ["meta pixel", "facebook", "instagram", "remarketing", "ads"],
    url: "/app/settings",
    content: `
**Qué resuelve:** te permite medir qué tan efectivos son tus anuncios de Facebook e Instagram, y armar audiencias de remarketing con la gente que visitó tu tienda o agregó productos al carrito.

**Cuándo conviene usarla:** si invertís (o pensás invertir) en Meta Ads.

**Requisitos previos:** un Pixel creado en el Administrador de eventos de Meta Business Suite (te da un ID numérico).

**Dónde encontrarla:** **Configuración → Medición → Meta Pixel** (grupo Publicidad).

**Configuración paso a paso:**
1. En Meta Business Suite → Administrador de eventos, copiá el ID de tu Pixel.
2. En Structura, abrí la tarjeta de Meta Pixel, activá el switch y pegá el ID.
3. Guardá.

**Qué debe completar el comercio:** el ID del Pixel.

**Qué sucede después de guardar:** el Pixel se inyecta en la tienda pública y empieza a reportar vistas de página, vistas de producto y compras a Meta.

**Cómo verificar que quedó funcionando:** instalá la extensión de Chrome "Meta Pixel Helper", abrí tu tienda pública y confirmá que detecta el Pixel activo y los eventos que dispara.

**Errores frecuentes:**
- Pegar el ID de una cuenta publicitaria en vez del ID del Pixel — son números distintos dentro de Meta Business Suite.
- No verificar el dominio de tu tienda en Meta Business Suite, lo que puede limitar la efectividad del pixel en iOS 14.5+.

**Límites o plan:** disponible desde plan Pro.

**Cómo desactivar o cambiar:** apagá el switch de la tarjeta; el Pixel deja de dispararse en la próxima carga de la tienda.

**Preguntas frecuentes:**
- *¿Necesito el SDK de conversiones de la API (server-side)?* No, esta integración es sólo el pixel del lado del cliente (browser).
`,
  },
  {
    id: "tiktok-pixel-connect",
    title: "Conectar TikTok Pixel",
    section: "Integraciones",
    sectionIcon: "💬",
    keywords: ["tiktok", "pixel", "ads", "conversiones"],
    url: "/app/settings",
    content: `
**Qué resuelve:** mide el rendimiento de tus campañas de TikTok Ads y te permite optimizar conversiones (compras, agregados al carrito) desde tu tienda.

**Cuándo conviene usarla:** si corrés o planeás correr campañas en TikTok Ads.

**Requisitos previos:** un Pixel creado desde TikTok Ads Manager (te da un Pixel ID).

**Dónde encontrarla:** **Configuración → Medición → TikTok Pixel** (grupo Publicidad).

**Configuración paso a paso:**
1. En TikTok Ads Manager → Herramientas → Eventos, creá o copiá tu Pixel ID.
2. En Structura, abrí la tarjeta de TikTok Pixel, activá el switch y pegá el ID.
3. Guardá.

**Qué debe completar el comercio:** el Pixel ID.

**Qué sucede después de guardar:** el pixel se inyecta en la tienda pública y reporta eventos de navegación y compra a TikTok Ads.

**Cómo verificar que quedó funcionando:** usá la extensión "TikTok Pixel Helper" en Chrome sobre tu tienda pública, o revisá el estado del pixel en TikTok Ads Manager (debería pasar de "No verificado" a "Activo" después de recibir tráfico).

**Errores frecuentes:**
- Copiar el ID de una campaña en vez del Pixel ID.
- Esperar eventos inmediatos en el Ads Manager — puede demorar hasta unas horas en reflejar actividad reciente.

**Límites o plan:** disponible desde plan Pro.

**Cómo desactivar o cambiar:** apagá el switch de la tarjeta.

**Preguntas frecuentes:**
- *¿Sirve sin tener campañas activas todavía?* Sí, podés dejarlo conectado para empezar a acumular datos de audiencia antes de lanzar campañas.
`,
  },
  {
    id: "pinterest-tag-connect",
    title: "Conectar Pinterest Tag",
    section: "Integraciones",
    sectionIcon: "💬",
    keywords: ["pinterest", "tag", "ads", "shopping", "catalogo"],
    url: "/app/settings",
    content: `
**Qué resuelve:** mide conversiones de tus campañas de Pinterest Ads y habilita que tu catálogo de productos aparezca en Pinterest Shopping.

**Cuándo conviene usarla:** si tu público objetivo usa Pinterest para buscar inspiración de compra (deco, moda, gastronomía, regalos) o si corrés Pinterest Ads.

**Requisitos previos:** una cuenta de Pinterest Business con un Tag creado desde Pinterest Ads Manager → Conversiones (te da un Tag ID).

**Dónde encontrarla:** **Configuración → Medición → Pinterest** (grupo Publicidad).

**Configuración paso a paso:**
1. En Pinterest Ads Manager, copiá el Tag ID.
2. En Structura, abrí la tarjeta de Pinterest, activá el switch y pegá el ID.
3. Guardá.

**Qué debe completar el comercio:** el Tag ID.

**Qué sucede después de guardar:** el tag se inyecta en la tienda pública y reporta vistas y conversiones a Pinterest.

**Cómo verificar que quedó funcionando:** usá la extensión "Pinterest Tag Helper" sobre tu tienda pública.

**Errores frecuentes:**
- Confundir el Tag ID con el ID de la cuenta de anuncios.

**Límites o plan:** disponible desde plan Pro.

**Cómo desactivar o cambiar:** apagá el switch de la tarjeta.

**Preguntas frecuentes:**
- *¿Esto sube mi catálogo a Pinterest automáticamente?* No, el tag mide conversiones; la sincronización del catálogo de productos a Pinterest Shopping se configura aparte, del lado de Pinterest Business.
`,
  },
  {
    id: "google-ads-conversion-connect",
    title: "Conectar Google Ads (medición de conversiones)",
    section: "Integraciones",
    sectionIcon: "💬",
    keywords: ["google ads", "conversion", "aw-", "campañas"],
    url: "/app/settings",
    content: `
**Qué resuelve:** mide cuántas compras generan tus campañas de Google Ads, directamente desde el checkout de tu tienda.

**Cuándo conviene usarla:** si invertís en Google Ads (Search, Shopping o Display) y necesitás saber qué campañas realmente venden.

**Requisitos previos:** una acción de conversión creada en Google Ads (Herramientas → Conversiones), que te da un ID de conversión con formato \`AW-XXXXXXXX\`.

**Dónde encontrarla:** **Configuración → Medición → Google Ads** (grupo Publicidad).

**Configuración paso a paso:**
1. En Google Ads → Conversiones, copiá el ID (\`AW-XXXXXXXX\`).
2. En Structura, abrí la tarjeta de Google Ads, activá el switch y pegá el ID.
3. Guardá.

**Qué debe completar el comercio:** el ID de conversión.

**Qué sucede después de guardar:** el checkout de la tienda pública reporta la conversión a Google Ads cada vez que se completa una compra.

**Cómo verificar que quedó funcionando:** hacé una compra de prueba en tu tienda pública y revisá en Google Ads → Conversiones que el conteo suba (puede demorar unas horas en reflejarse).

**Errores frecuentes:**
- Usar el ID de medición de GA4 (\`G-XXXXXXXX\`) en este campo — son integraciones distintas con formatos de ID distintos.

**Límites o plan:** disponible desde plan Pro.

**Cómo desactivar o cambiar:** apagá el switch de la tarjeta.

**Preguntas frecuentes:**
- *¿Necesito tener GA4 activo también?* No son dependientes entre sí, pero usarlos juntos te da una vista más completa: GA4 para comportamiento, Google Ads para atribución de conversión.
`,
  },
  {
    id: "hotjar-connect",
    title: "Conectar Hotjar",
    section: "Integraciones",
    sectionIcon: "💬",
    keywords: ["hotjar", "heatmap", "grabaciones", "sesiones", "encuestas"],
    url: "/app/settings",
    content: `
**Qué resuelve:** te muestra grabaciones de sesiones reales, mapas de calor (heatmaps) y encuestas para entender cómo navegan tus clientes en la tienda — dónde hacen clic, dónde se traban, dónde abandonan.

**Cuándo conviene usarla:** cuando querés entender por qué una pantalla no convierte, más allá de los números que da GA4.

**Requisitos previos:** una cuenta de Hotjar con un sitio creado, que te da un Site ID.

**Dónde encontrarla:** **Configuración → Medición → Hotjar** (grupo Analítica).

**Configuración paso a paso:**
1. En Hotjar → Sites & Organizations, copiá el Site ID de tu sitio.
2. En Structura, abrí la tarjeta de Hotjar, activá el switch y pegá el ID.
3. Guardá.

**Qué debe completar el comercio:** el Site ID.

**Qué sucede después de guardar:** el script de Hotjar se inyecta en la tienda pública y empieza a grabar sesiones y generar heatmaps según la configuración que tengas en tu cuenta de Hotjar.

**Cómo verificar que quedó funcionando:** navegá tu tienda pública desde otra pestaña/dispositivo y revisá en Hotjar → Recordings que aparezca la sesión (puede demorar unos minutos).

**Errores frecuentes:**
- Pegar la URL del sitio en vez del Site ID numérico.

**Límites o plan:** disponible desde plan Pro. Hotjar tiene también sus propios límites de plan gratuito/pago (cantidad de sesiones grabadas por día) — eso se administra del lado de Hotjar, no de Structura.

**Cómo desactivar o cambiar:** apagá el switch de la tarjeta.

**Preguntas frecuentes:**
- *¿Hotjar ve datos personales de mis clientes?* Hotjar enmascara por defecto campos sensibles (inputs de texto, números de tarjeta); revisá la configuración de privacidad dentro de tu cuenta de Hotjar si necesitás ajustar esto.
`,
  },
  {
    id: "clarity-connect",
    title: "Conectar Microsoft Clarity",
    section: "Integraciones",
    sectionIcon: "💬",
    keywords: ["clarity", "microsoft", "heatmap", "grabaciones", "gratis"],
    url: "/app/settings",
    content: `
**Qué resuelve:** igual que Hotjar, te da heatmaps y grabaciones de sesión para entender el comportamiento de tus clientes — con la ventaja de que Microsoft Clarity es gratuito sin límite de sesiones.

**Cuándo conviene usarla:** si querés esta clase de análisis sin pagar un plan de Hotjar, o además de Hotjar para comparar.

**Requisitos previos:** un proyecto creado en [clarity.microsoft.com](https://clarity.microsoft.com), que te da un Project ID.

**Dónde encontrarla:** **Configuración → Medición → Microsoft Clarity** (grupo Analítica).

**Configuración paso a paso:**
1. En Clarity, creá un proyecto y copiá el Project ID.
2. En Structura, abrí la tarjeta de Microsoft Clarity, activá el switch y pegá el ID.
3. Guardá.

**Qué debe completar el comercio:** el Project ID.

**Qué sucede después de guardar:** el script de Clarity se inyecta en la tienda pública y empieza a grabar sesiones y generar heatmaps.

**Cómo verificar que quedó funcionando:** navegá tu tienda pública y revisá en el dashboard de Clarity que aparezcan sesiones nuevas (suele tardar pocos minutos).

**Errores frecuentes:**
- Confundir el Project ID con la URL del dashboard.

**Límites o plan:** disponible desde plan Pro.

**Cómo desactivar o cambiar:** apagá el switch de la tarjeta.

**Preguntas frecuentes:**
- *¿Puedo tener Clarity y Hotjar activos a la vez?* Sí, son independientes.
`,
  },
];
